import React from "react";
import { useTranslation } from "react-i18next";

import "./favoritetoggle.css";

/**
 * The favourite heart, in the two shapes the professional flow needs:
 *
 *   variant="icon" — the round button at the top-right of a medium info card.
 *   variant="pill" — the labelled control in the medium profile header, where
 *                    there is room to say what the heart does.
 *
 * Presentational on purpose. It renders whatever `isFavorite` it is handed and
 * reports taps; the owner of the list holds the state, fires the request and
 * rolls back on failure.
 */
export default function FavoriteToggle({
  isFavorite = false,
  onToggle = () => {},
  disabled = false,
  variant = "icon",
  className = "",
}) {
  const { t } = useTranslation("clientPremium");
  const label = isFavorite ? t("favorite.remove") : t("favorite.add");
  const base =
    variant === "pill" ? "medium-favorite-pill" : "medium-favorite-toggle";

  return (
    <button
      type="button"
      className={`${base} ${isFavorite ? "is-favorite" : ""} ${className}`}
      aria-pressed={isFavorite ? "true" : "false"}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={(e) => {
        // The heart sits inside clickable regions on both screens - keep the tap
        // here rather than letting it open the card or the profile behind it.
        e.stopPropagation();
        onToggle();
      }}
    >
      <svg
        viewBox="0 0 24 24"
        fill={isFavorite ? "#D5A67B" : "none"}
        stroke={isFavorite ? "#D5A67B" : "#B9A996"}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l8.84 8.84 8.84-8.84a5.5 5.5 0 0 0 0-7.78z"></path>
      </svg>
      {variant === "pill" && (
        <span className="medium-favorite-pill-label">
          {isFavorite ? t("favorite.favorited") : t("favorite.favorite")}
        </span>
      )}
    </button>
  );
}
